import {
  Checkbox,
  Flex,
  Grid,
  GridItem,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import APButton from "../../../components/APButton";
import { HiOutlineDuplicate } from "react-icons/hi";
import { useVocabIntl } from "../../../hooks/useVocabTranslation";
import { getCanvasFont, getTagLabels, getTextWidth } from "../../../utils";
import { VocabScenario, VocabWord } from "../../../VocabModel";
import APIconButton from "../../../components/APIconButton";
import { Close } from "../../../components/APAssetAtlas";
import { BREAKPOINTS_VALUES } from "../../../theme/APChakraTheme";
import useScreenInformation from "../../../hooks/useScreenInformation";
import { IExerciserSDK } from "ap-exerciser-sdk";

interface ScenarioPreviewModalProps {
  open: boolean;
  scenario: VocabScenario | null;
  listDescription: JSX.Element | string;
  allowChecks?: boolean;
  onStart: (list: string | VocabWord[], skipPreview?: boolean) => void;
  onModify?: () => void;
  onClose?: () => void;
  sdk?: IExerciserSDK;
}

interface ScenarioWithReturn extends ScenarioPreviewModalProps {
  onReturn: () => void;
  returnTitle: string;
}

export default function ScenarioPreviewModal({
                                               open,
                                               listDescription,
                                               onClose,
                                               onStart,
                                               onModify,
                                               scenario,
                                               allowChecks,
                                               sdk,
                                               ...optional
                                             }: ScenarioPreviewModalProps | ScenarioWithReturn) {
  const { getFormattedString, APFormattedMessage } = useVocabIntl();
  const { width } = useScreenInformation();
  const [copied, setCopied] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null!);
  const scrollRef = useRef<HTMLDivElement>(null!);
  const [gridLayout, setGridLayout] = useState<{
    columnCount: number,
    font: string,
    cellWidth: number,
    initialized: boolean,
    scenarioId: string,
  }>({ columnCount: 1, font: "", cellWidth: 100, initialized: false, scenarioId: "" })

  const [hasScroll, setScroll] = useState(false);

  const [checkedWords, setCheckedWords] = useState(() => {
    if (allowChecks && scenario) {
      return scenario.wordsList.slice();
    }
    return [];
  });

  useEffect(() => {
    if (allowChecks && scenario) {
      setCheckedWords(scenario.wordsList.slice())
    }
  }, [scenario]);

  const copyClipboard = () => {
    if (scenario) {
      navigator.clipboard.writeText(scenario.id).then(
        function () {
          setCopied(true)
          setTimeout(() => {
            setCopied(false)
          }, 5000);
        },
        function (err) {}
      );
    }
  };

  const checkWord = (wordItem: VocabWord) => {
    const index = checkedWords.findIndex((wordToTest) => wordToTest.id === wordItem.id);
    if (index >= 0) {
      checkedWords.splice(index, 1);
    } else {
      checkedWords.push(wordItem);
    }
    setCheckedWords(checkedWords.slice())
  };

  const startExercise = () => {
    if (sdk) {
      sdk.getAnalytics().push({
        event: "exercise_start",
        action: "click",
        platform: process.env.REACT_APP_PLATFORM
      });
    }

    if (scenario && !allowChecks) {
      onStart(scenario.id)
    } else {
      onStart(checkedWords)
    }
  }

  const onGridMount = (gridElement: HTMLDivElement) => {
    if (gridElement == gridRef.current || open === gridLayout.initialized) {
      return;
    }
    gridRef.current = gridElement;
    if (!scenario || !gridElement || !open) {
      if (gridLayout.initialized) {
        setGridLayout({ columnCount: 1, font: "", cellWidth: 100, initialized: false, scenarioId: "" })
      }
    } else if (!gridLayout.initialized || scenario.id !== gridLayout.scenarioId) {
      let columnCount = 1;
      if (scenario.wordsList.length < 8) {
        columnCount = 1;
      } else if (scenario.wordsList.length < 16 || width <= BREAKPOINTS_VALUES['md']) { // Match grid breakpoint layout
        columnCount = 2;
      } else if (width > BREAKPOINTS_VALUES['md']) {
        columnCount = 3;
      }

      setGridLayout({
        columnCount,
        font: getCanvasFont(gridElement),
        cellWidth: gridElement.clientWidth / columnCount,
        initialized: true,
        scenarioId: scenario.id,
      })
    }
  }

  const onScrollMount = (flexElement: HTMLDivElement) => {
    if (flexElement == scrollRef.current) {
      return;
    }
    scrollRef.current = flexElement;

    if (!flexElement) {
      setScroll(false);
    } else {
      setScroll(flexElement.scrollHeight > flexElement.clientHeight)
    }
  }

  return (
    <Modal variant={"seamlessBody"} blockScrollOnMount={false} closeOnOverlayClick={false} isOpen={open}
           onClose={onClose ?? (() => {})}
           size={"md"}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader textAlign={"center"}>
            <Flex flexDir={"column"}>
              <Text fontWeight={700}>
                {scenario?.name}
              </Text>
              <Text fontSize={"md"} textAlign={"center"}>
                {scenario && getTagLabels(scenario.tags).join(", ")}
              </Text>
            </Flex>
          </ModalHeader>
          {onClose &&
              <APIconButton variant={"raisedIcon"} rounded={"full"} tabIndex={3} onClick={onClose}
                            position={"absolute"} right={"-0.75rem"} top={"-0.75rem"} fontSize={"1.5rem"}
                            aria-label={getFormattedString("aria-label-close-modal")}>
                <Close />
              </APIconButton>
          }
          <ModalBody>
            <HStack padding={["0.5rem 1rem 0 1rem", null, "1rem 1rem 0 1rem"]} width={"full"}>
              <Text fontSize={"lg"} fontWeight={"500"}>
                {listDescription}
              </Text>
              {onModify && <>
                <Spacer />
                <APButton size={"md"} onClick={onModify} variant={"solid"} colorScheme={"secondary"}
                          textDecoration={"underline"} fontWeight={"500"}>
                  <APFormattedMessage id={"modify-button"} />
                </APButton>
              </>}
            </HStack>
            <Flex layerStyle={"scrollContainer"} ref={onScrollMount} overflowY={"auto"} overflowX={"hidden"}
                  padding={"0.5rem 0.5rem 1rem 0.5rem"}
                  maxHeight={["30vh", "50vh", "32vh"]}
                  minHeight={"4rem"}>
              <Grid ref={onGridMount} width={"full"} maxH={"100%"} maxW={"100%"}
                    height={"fit-content"}
                    className={"wordlist"}
                    templateColumns={[`repeat(min(${gridLayout.columnCount}, 2), 1fr)`, null, `repeat(min(${gridLayout.columnCount}, 3), 1fr)`]}
                    gap={1} autoRows={['auto']}>
                {scenario && gridLayout.initialized && scenario.wordsList.map((word, idx) => {
                  //Add W to width calculation for checkbox and gap padding;
                  const textWidth = getTextWidth(`W ${word.label} `, gridLayout.font);
                  const columnSpan = Math.min(Math.ceil(textWidth / gridLayout.cellWidth), gridLayout.columnCount);
                  return (
                    <GridItem key={idx} display={"flex"} flexDirection={"row"} gap={2}
                              justifyContent={"left"}
                              colSpan={columnSpan}>
                      <Checkbox variant={"flushed"} colorScheme={"primary"} isDisabled={!allowChecks}
                                size={allowChecks ? "md" : "sm"}
                                onChange={() => checkWord(word)}
                                isChecked={checkedWords.some(
                                  (checkedWord) => checkedWord.id === word.id
                                )} />
                      <Text alignSelf={"center"}>{word.label}</Text>
                      {/*<div style={{ width: "0.75em" }} />*/}
                    </GridItem>
                  )
                })}
              </Grid>
              {hasScroll && <Flex layerStyle={"scrollShadow"} />}
            </Flex>
          </ModalBody>
          <ModalFooter display={"flex"} justifyContent={"center"}>
            {scenario && scenario.id && !scenario.id.includes("guest") ? (
              <Flex flexDirection={"column"} alignItems={"center"} gap={[2, 4]} width={"100%"}>
                <Flex flexDirection={{ base: "column", sm: "row" }} alignItems={"center"} gap={2} width={"100%"}>
                  <APButton variant={"outline"} size={"md"} colorScheme={"tertiary"} rounded={"md"}
                            onClick={() => copyClipboard()}>
                    <HiOutlineDuplicate size={"1.25em"} style={{ minWidth: "1.25em" }} color="currentColor" />
                    <Text>
                      {copied ?
                        getFormattedString("copy-code-button-copied") :
                        `${getFormattedString("copy-code-button")}: ${scenario.id}`
                      }
                    </Text>
                  </APButton>
                  <APButton
                    onClick={startExercise}
                    colorScheme="primary"
                    paddingX={"2.5rem"}
                    aria-label={getFormattedString("aria-label-start-button")}>
                    <APFormattedMessage id={"start-button"} />
                  </APButton>
                </Flex>
                {"onReturn" in optional &&
                    <APButton
                        onClick={optional.onReturn}
                        paddingX={"2.5rem"}
                        colorScheme="secondary"
                        aria-label={optional.returnTitle}>
                      {optional.returnTitle}
                    </APButton>
                }
              </Flex>
            ) : (
              <Flex flexDirection={"row"} justifyContent={"space-between"} gap={2} width={"100%"}>
                {"onReturn" in optional ?
                  <APButton
                    onClick={optional.onReturn}
                    paddingX={"2.5rem"}
                    colorScheme="secondary"
                    aria-label={optional.returnTitle}>
                    {optional.returnTitle}
                  </APButton> : <div />
                }
                <APButton
                  onClick={startExercise}
                  colorScheme="primary"
                  paddingX={"2.5rem"}
                  aria-label={getFormattedString("aria-label-start-button")}>
                  <APFormattedMessage id={"start-button"} />
                </APButton>
              </Flex>
            )}
          </ModalFooter>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
    ;
}
